import type { EngineStageConfig } from '@/app/lcv/components/sections/spartan/engine/sections/hero-animation/webgpu/engine-stage/config/types';

import { STORY_AURA_CONFIG } from './story-aura';

// THE ENGINE'S AURA, as authored.
//
// Every LOOK value here is carried over unchanged from what was
// `ENGINE_BACKDROP_CONFIG.ring`, so the field is the one that was already
// tuned. Only the two SIZE values have new meaning, because their unit
// changed from the screen's diagonal to the engine's own width.
export const ENGINE_STAGE_CONFIG: EngineStageConfig = {
  aura: {
    // THREE ENGINES ACROSS.
    //
    // Chosen to land near the quad size the look was tuned at: the old
    // screen-diagonal quad was about 14 world units on a 16:9 desktop, and
    // three times the story engine's side is a little under that. So the
    // field reads much as before at the size it was authored against --
    // and now shrinks and grows with the engine, which it never did.
    sizeMultiplier: 3.5,
    liftMultiplier: 0.02,

    zoom: 1.1,
    flowSpeed: 0.045,
    noiseScale: 1.75,
    warpStrength: 1.45,
    swirlSpeed: 0.85,
    frequency: 5.3,
    pulseSpeed: 0,
    baseColor: '#ff0000',
    vignetteFeather: 0.18,
    // Sharper red filaments, with enough energy to hold between bursts.
    opacity: 1.5,
    // `dualFbm` runs `ridgedFbm` THREE times (two to displace the domain,
    // one for the result), so this is multiplied by three: 9 perlin
    // evaluations per pixel. The most expensive dial here by far -- and it
    // now costs far less in total than it did, because the quad is a few
    // engine-widths rather than the whole screen.
    octaves: 3,
    // Nothing drives the field sideways, so a second sample lands on top of
    // the first: double the cost, identical picture.
    trailSamples: 1,
    trailLength: 0.18,
    trailDecay: 0.21,
    // The intro's `ring.settle` beat -- where the ring came to rest.
    ringScale: 13.5,
    brightness: 0.8,

    // A quick crest followed by a longer release matches the ground impulse.
    breathMin: 0.56,
    breathMax: 1.75,
    // The ring's own loop. It used to be matched to the spark set's, which
    // is gone; the lightning runs on its own cycle and the two are
    // deliberately independent now -- see EngineLightning.
    breathSeconds: 6,
    breathPeakAt: 0.2,

    gamma: 0.6,
    sharpness: 2.5,
    // WHERE THE FIELD FADES OUT, as a fraction of the quad -- and its
    // meaning has changed with the quad.
    //
    // Tuned when the quad was the screen's diagonal, where the frame's
    // corners sat at 0.5, so this began the falloff just before the effect
    // left the frame. On an engine-sized quad the same number now fades the
    // field at about 1.4 engine-widths from centre, which is what turns it
    // from a full-screen wash into a halo around the engine. That is the
    // intent, but it is the first dial to reach for if the aura reads too
    // tight or too loose.
    vignetteRadius: 0.475,
    // THE CORE IS OFF. The log-polar singularity reads as a small blown-out
    // disc sitting on top of the rings rather than as a ring of its own --
    // and directly behind the engine is the worst possible place for it.
    coreOpacity: 0,
    coreRadius: 0.06,
    coreFeather: 0.08,
  },

  storyAura: STORY_AURA_CONFIG,
};
